<div class="flex flex-column gap-3" data-test="ui-ql-inspector">

    <!-- policy verdict -->
    <div class="flex flex-column gap-2 p-3 border-1 surface-border border-round-lg">
        <span class="font-semibold">Regelprüfung</span>
        @if (validating) {
            <div class="flex flex-column gap-2">
                <p-skeleton height="1.5rem" width="40%"></p-skeleton>
                <p-skeleton height="1.5rem" width="70%"></p-skeleton>
            </div>
        } @else if (validationError) {
            <p-message severity="error" size="small" styleClass="w-full" [text]="validationError"
                       data-test="ui-ql-inspector-validation-error"></p-message>
        } @else if (validation; as v) {
            <div class="flex flex-wrap align-items-center gap-2">
                @if (v.valid) {
                    <p-tag severity="success" value="Gültig – speicherbar" icon="fa-regular fa-circle-check"
                           data-test="ui-ql-inspector-valid"></p-tag>
                } @else {
                    <p-tag severity="danger" value="Ungültig – Speichern gesperrt" icon="fa-regular fa-circle-xmark"
                           data-test="ui-ql-inspector-invalid"></p-tag>
                }
                @if (v.envBound) {
                    <p-tag severity="warn" value="umgebungsgebunden" icon="fa-regular fa-link"
                           pTooltip="Die Definition enthält umgebungsabhängige IDs und ist nicht zwischen Umgebungen übertragbar."></p-tag>
                }
            </div>
            @if (v.violations?.length) {
                <ul class="m-0 pl-3 flex flex-column gap-1" data-test="ui-ql-inspector-violations">
                    @for (violation of v.violations; track $index) {
                        <li>
                            {{ violationText(violation) }}
                            <span class="text-sm text-color-secondary ml-1">({{ violation.code }} · {{ violation.path }})</span>
                        </li>
                    }
                </ul>
            }
        } @else {
            <span class="text-sm text-color-secondary">Noch kein Prüfergebnis – es erscheint, sobald eine Abfrage definiert ist.</span>
        }
    </div>

    <!-- execution -->
    <div class="flex flex-column gap-2 p-3 border-1 surface-border border-round-lg">
        <span class="font-semibold">Ausführung</span>
        @if (!previewState || previewState.phase === 'empty') {
            <span class="text-sm text-color-secondary">Noch keine Abfrage ausgeführt. Die Vorschau führt sie automatisch aus.</span>
        } @else {
            <div class="flex flex-wrap align-items-center gap-3 text-sm">
                <span data-test="ui-ql-inspector-phase"><i class="fa-regular fa-circle-play mr-1"></i>{{ phaseLabel }}</span>
                @if (previewState.widget; as widget) {
                    <p-tag [value]="'Pipe: ' + widget.pipe" severity="secondary"></p-tag>
                    <p-tag [value]="widget.includeCount ? 'mit Gesamtanzahl' : 'ohne Gesamtanzahl'" severity="secondary"></p-tag>
                    <span>Seitengröße {{ widget.pageSize }}</span>
                }
            </div>

            @if (previewState.phase === 'ready') {
                @if (previewStatus; as status) {
                    <div class="flex flex-column gap-1 text-sm">
                        <span data-test="ui-ql-inspector-count">
                            <i class="fa-regular fa-list mr-1"></i>{{ countText }}
                            @if (countUnavailableReason) {
                                <span class="text-color-secondary">({{ countUnavailableReason }})</span>
                            }
                        </span>
                        <span data-test="ui-ql-inspector-runtime">
                            <i class="fa-regular fa-stopwatch mr-1"></i>
                            @if (status.runtimeMs != null) {
                                {{ status.runtimeMs }} ms
                                @switch (status.runtimeSource) {
                                    @case ('engine') { <span class="text-color-secondary">(Engine)</span> }
                                    @case ('pipe') { <span class="text-color-secondary">(Pipe)</span> }
                                    @case ('client') { <span class="text-color-secondary">(im Browser gemessen)</span> }
                                }
                            } @else {
                                Laufzeit unbekannt
                            }
                        </span>
                    </div>
                    @if (status.pipeErrors.length) {
                        <p-message severity="error" styleClass="w-full" data-test="ui-ql-inspector-pipe-errors">
                            <div class="flex flex-column gap-1">
                                <span class="font-semibold">Pipe-Meldungen (HTTP 200):</span>
                                @for (pipeError of status.pipeErrors; track $index) {
                                    <span>{{ pipeError }}</span>
                                }
                            </div>
                        </p-message>
                    } @else {
                        <span class="text-sm text-color-secondary">Keine Pipe-Meldungen.</span>
                    }
                } @else {
                    <div class="flex flex-column gap-2">
                        <p-skeleton height="1.25rem" width="50%"></p-skeleton>
                        <p-skeleton height="1.25rem" width="30%"></p-skeleton>
                    </div>
                }
            }

            @if (previewState.phase === 'violations' && previewState.violations.length) {
                <ul class="m-0 pl-3 flex flex-column gap-1 text-sm">
                    @for (violation of previewState.violations; track $index) {
                        <li>{{ violationText(violation) }}</li>
                    }
                </ul>
            }
            @if (previewState.phase === 'invalid') {
                <ul class="m-0 pl-3 flex flex-column gap-1 text-sm">
                    @for (error of previewState.widget?.errors ?? []; track $index) {
                        <li>{{ error.message }}</li>
                    }
                </ul>
            }
            @if (previewState.phase === 'error' && previewState.errorMessage) {
                <p-message severity="error" size="small" styleClass="w-full" [text]="previewState.errorMessage"></p-message>
            }
        }
    </div>

    <!-- request -->
    <div class="flex flex-column gap-2 p-3 border-1 surface-border border-round-lg">
        <span class="font-semibold">Gesendeter Request (/core/ql/query)</span>
        @if (requestJson) {
            <pre class="ui-ql-inspector-code overflow-x-auto m-0 p-3 surface-50 border-1 surface-border border-round-lg"
                 data-test="ui-ql-inspector-request">{{ requestJson }}</pre>
        } @else {
            <span class="text-sm text-color-secondary">Es wurde noch kein Request gesendet.</span>
        }
    </div>
</div>
